import React from 'react';
import { STAGE_LABELS, STAGE_GATES, STAGE_ADVANCE_LABELS, CanonicalStage } from '../../../lib/workflow/transitions';
import type { StagePrerequisite } from '../../../lib/workflow/types';

/**
 * Shared advance-action derivation — the gate panel's button and the
 * drawer's pinned footer action must agree EXACTLY: same visibility
 * condition and same stage label. Single source so the two surfaces can
 * never drift apart again.
 */
export function advanceButtonLabel(stage: string): string {
  return (STAGE_ADVANCE_LABELS as any)[stage as CanonicalStage] || 'Advance';
}

export function shouldShowAdvanceButton(readyToAdvance: boolean, overridden: boolean): boolean {
  return !!readyToAdvance && !overridden;
}

interface StageGatePanelProps {
  stage: string;
  applicantName: string;
  prerequisites: StagePrerequisite[];
  readyToAdvance: boolean;
  overridden: boolean;
  /** Applicant was manually moved back to an earlier stage (amber badge). */
  demoted?: boolean;
  demoteReason?: string | null;
  isSeniorAdmin: boolean;
  advancing: boolean;
  onAdvance: (closure?: { closureType: string; closureReason?: string }) => void;
  onOverride: () => void;
  /** Optional: open the demote-to-earlier-stage dialog. */
  onDemote?: () => void;
  /** Optional: open the AI stage-email composer. */
  onDraftEmail?: () => void;
  /** Stage-specific primary action (e.g. pre-approval buttons for submitted). Rendered below the checklist. */
  actionSlot?: React.ReactNode;
  /** PR-M: clear a POTENTIAL redlist match for this applicant (persistent,
   * fingerprinted, senior-admin server-side). The drawer owns the fetch. */
  onClearRedlist?: (note: string) => void;
  /** PR-M: revoke a redlist clearance previously granted for this applicant. */
  onRevokeRedlist?: () => void;
  /** PR-M: a redlist clear/revoke request is in flight. */
  redlistBusy?: boolean;
}

/**
 * Stage-incremental gate panel (refined spec 2026-10-11).
 *
 * Shows ONLY the current stage's prerequisites — never other stages'.
 * All state comes from the server contract (record.stagePrerequisites);
 * this component never computes stage or readiness.
 */
export default function StageGatePanel({
  stage,
  applicantName: _applicantName,
  prerequisites,
  readyToAdvance,
  overridden,
  demoted,
  demoteReason,
  isSeniorAdmin,
  advancing,
  onAdvance,
  onOverride,
  onDemote,
  onDraftEmail,
  actionSlot,
  onClearRedlist,
  onRevokeRedlist,
  redlistBusy,
}: StageGatePanelProps) {
  const canonical = stage as CanonicalStage;
  const stageLabel = (STAGE_LABELS as any)[canonical] || stage;
  const advanceLabel = advanceButtonLabel(stage);
  const gates = (STAGE_GATES as any)[canonical] || [];

  const unmet = prerequisites.filter((p) => !p.met);
  const metCount = prerequisites.length - unmet.length;
  const allMet = prerequisites.length > 0 && unmet.length === 0;

  // PR-M: redlist clearance form (potential matches only). The note is
  // required at ≥20 chars — the same standard as a gate-override reason,
  // enforced again server-side.
  const [redlistFormOpen, setRedlistFormOpen] = React.useState(false);
  const [redlistNote, setRedlistNote] = React.useState('');

  // Closure type selection — required when closing from preDeparture.
  // Never auto-assumed; the admin explicitly sets SD/SC/SP.
  const [closureType, setClosureType] = React.useState('SD');
  const [closureReason, setClosureReason] = React.useState('');
  const isClosing = canonical === 'preDeparture';

  const handleAdvanceClick = () => {
    if (isClosing) {
      onAdvance({ closureType, closureReason: closureReason.trim() || undefined });
    } else {
      onAdvance();
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      {/* Header — APPLICANT GATES group (the stage's applicant-side
          requirements). The ADMIN GATES group (phase admin tasks) is
          the sibling panel below; the two never mix. */}
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
            Applicant gates
          </div>
          <h3 className="text-sm font-bold text-slate-900">{stageLabel}</h3>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {prerequisites.length > 0 && (
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold tabular-nums ${
                allMet ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-800'
              }`}
              aria-label={`${metCount} of ${prerequisites.length} applicant gates met`}
            >
              {metCount}/{prerequisites.length} met
            </span>
          )}
          {overridden && (
            <span className="inline-flex items-center rounded-full bg-purple-100 px-2.5 py-0.5 text-[11px] font-bold text-purple-700">
              Overridden
            </span>
          )}
          {demoted && (
            <span
              className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-[11px] font-bold text-amber-800"
              title={demoteReason || 'Manually moved back to an earlier stage'}
            >
              Demoted
            </span>
          )}
        </div>
      </div>

      {/* Prerequisites — current stage ONLY */}
      <div className="mt-3 space-y-2">
        {prerequisites.length === 0 && (
          <p className="text-xs text-slate-500">No prerequisites for this stage.</p>
        )}
        {prerequisites.map((p) => {
          const conditionMet = (p as any).conditionMet !== undefined ? !!(p as any).conditionMet : p.met;
          const tone = p.overridden
            ? 'border-purple-200 bg-purple-50/60'
            : p.met
              ? 'border-emerald-200 bg-emerald-50/60'
              : 'border-amber-200 bg-amber-50/60';
          return (
            <div key={p.key} className={`rounded-lg border px-3 py-2 ${tone}`}>
              <div className="flex items-start gap-2.5">
                <span
                  className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${
                    p.overridden ? 'bg-purple-500 text-white' : p.met ? 'bg-emerald-500 text-white' : 'bg-amber-400 text-white'
                  }`}
                  aria-label={p.overridden ? 'Overridden' : p.met ? 'Met' : 'Not met'}
                >
                  {p.met ? '✓' : '!'}
                </span>
                <div className="min-w-0">
                  <div className={`flex flex-wrap items-center gap-1.5 text-[13px] font-semibold ${
                    p.overridden ? 'text-purple-900' : p.met ? 'text-emerald-900' : 'text-amber-900'
                  }`}>
                    {p.label}
                    {p.overridden && (
                      <span className="rounded-full bg-purple-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                        Overridden
                      </span>
                    )}
                  </div>
                  {p.detail && (
                    <div className="text-xs text-slate-500">{p.detail}</div>
                  )}
                  {p.overridden && !conditionMet && (
                    <div className="mt-1 text-[11px] font-medium text-purple-700">
                      Underlying check is still unmet — this gate was bypassed by override.
                    </div>
                  )}
                </div>
              </div>

              {/* Override audit: who overrode this gate, when, and why — visible to every admin. */}
              {p.overridden && p.overrideReason && (
                <div className="mt-2 rounded-lg border border-purple-200 bg-white/70 px-2.5 py-2 text-[11px] text-purple-900">
                  <span className="font-bold">Override reason:</span> {p.overrideReason}
                  {(p.overriddenBy || p.overriddenAt) && (
                    <span className="block mt-0.5 text-purple-500">
                      {p.overriddenBy ? `by ${p.overriddenBy}` : ''}
                      {p.overriddenAt ? ` · ${new Date(p.overriddenAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}` : ''}
                    </span>
                  )}
                </div>
              )}

              {/* PR-M: redlist clearance — persistent, fingerprinted clearance
                  of a POTENTIAL match, in the gate area where the match is
                  displayed. High-confidence matches get no affordance here:
                  they are not clearable through this mechanism. */}
              {p.key === 'redlist_clear' && p.redlist && p.redlist.cleared && p.redlist.clearance && (
                <div className="mt-2 rounded-lg border border-emerald-200 bg-white/70 px-2.5 py-2 text-[11px] text-emerald-900">
                  <span className="font-bold">Potential match cleared</span>
                  <span className="block mt-0.5 text-emerald-600">
                    by {p.redlist.clearance.clearedByName || p.redlist.clearance.clearedBy}
                    {p.redlist.clearance.clearedAt ? ` on ${new Date(p.redlist.clearance.clearedAt).toLocaleDateString(undefined, { dateStyle: 'medium' })}` : ''}
                  </span>
                  {p.redlist.clearance.note && (
                    <span className="block mt-0.5">“{p.redlist.clearance.note}”</span>
                  )}
                  {isSeniorAdmin && onRevokeRedlist && (
                    <button
                      type="button"
                      onClick={onRevokeRedlist}
                      disabled={!!redlistBusy}
                      className="mt-1.5 rounded-lg border border-emerald-300 bg-white px-2.5 py-1 text-[11px] font-bold text-emerald-800 hover:bg-emerald-50 disabled:opacity-50 cursor-pointer"
                    >
                      {redlistBusy ? 'Revoking…' : 'Revoke clearance'}
                    </button>
                  )}
                </div>
              )}
              {p.key === 'redlist_clear' && p.redlist && !p.redlist.cleared && p.redlist.status === 'potential' && isSeniorAdmin && onClearRedlist && (
                <div className="mt-2">
                  {!redlistFormOpen ? (
                    <button
                      type="button"
                      onClick={() => setRedlistFormOpen(true)}
                      className="rounded-lg border border-amber-300 bg-white px-2.5 py-1.5 text-[11px] font-bold text-amber-800 hover:bg-amber-50 cursor-pointer"
                    >
                      Clear potential match — reviewed, not the same person
                    </button>
                  ) : (
                    <div className="rounded-lg border border-amber-200 bg-white/70 px-2.5 py-2">
                      <label className="block text-[11px] font-bold text-amber-900" htmlFor="redlist-clearance-note">
                        Clearance note <span className="font-medium text-amber-600">(required, at least 20 characters — it is stored with your name on the applicant's record)</span>
                      </label>
                      <textarea
                        id="redlist-clearance-note"
                        value={redlistNote}
                        onChange={(e) => setRedlistNote(e.target.value)}
                        rows={2}
                        placeholder="Why is this potential match not the same person?"
                        className="mt-1 w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                      />
                      <div className="mt-1.5 flex items-center gap-2">
                        <button
                          type="button"
                          disabled={redlistNote.trim().length < 20 || !!redlistBusy}
                          onClick={() => onClearRedlist(redlistNote.trim())}
                          className="rounded-lg bg-amber-600 px-3 py-1.5 text-[11px] font-bold text-white hover:bg-amber-700 disabled:opacity-50 cursor-pointer"
                        >
                          {redlistBusy ? 'Clearing…' : 'Record clearance'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setRedlistFormOpen(false)}
                          disabled={!!redlistBusy}
                          className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-bold text-slate-600 hover:bg-slate-50 disabled:opacity-50 cursor-pointer"
                        >
                          Cancel
                        </button>
                        <span className="text-[11px] text-slate-500">{redlistNote.trim().length}/20</span>
                      </div>
                      <p className="mt-1 text-[11px] text-slate-500">
                        Covers exactly this match set — a new matching entry or a change to the applicant's name, email or phone will require a fresh review.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* AI eligibility verdict (cached on the workflow record; Recompute re-runs it). */}
              {p.aiVerdict && (
                <div className="mt-2 rounded-lg border border-violet-200 bg-violet-50/70 px-2.5 py-2 text-[11px] text-violet-950">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="font-bold uppercase tracking-wide text-violet-500">AI eligibility</span>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                      p.aiVerdict.verdict === 'eligible'
                        ? 'bg-emerald-100 text-emerald-700'
                        : p.aiVerdict.verdict === 'ineligible'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-amber-100 text-amber-800'
                    }`}>
                      {p.aiVerdict.verdict}
                    </span>
                    {typeof p.aiVerdict.confidence === 'number' && (
                      <span className="text-violet-400">{Math.round(p.aiVerdict.confidence * 100)}% confidence</span>
                    )}
                  </div>
                  <p className="mt-1 font-medium">{p.aiVerdict.reason}</p>
                  {p.aiVerdict.verdict === 'undetermined' && p.aiVerdict.missingDetail && (
                    <p className="mt-0.5"><span className="font-bold">Missing detail:</span> {p.aiVerdict.missingDetail}</p>
                  )}
                  <p className="mt-0.5 text-violet-400">
                    {p.aiVerdict.model}
                    {p.aiVerdict.evaluatedAt ? ` · evaluated ${new Date(p.aiVerdict.evaluatedAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}` : ''}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Pre-Vetted badge — submitted stage only, when all 6 pre-vetting checks pass */}
      {canonical === 'submitted' && allMet && !overridden && (
        <div className="mt-3 flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-1.5 text-white">
          <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-white/20 text-[10px] font-bold">
            ✓
          </span>
          <span className="text-xs font-bold">Pre-Vetted</span>
          <span className="text-[11px] text-blue-100">— all checks passed, ready for admin decision</span>
        </div>
      )}

      {/* Ready-to-matriculate indicator */}
      {allMet && !overridden && (
        <div className="mt-3 flex items-center gap-2 rounded-lg bg-success px-3 py-1.5 text-white">
          <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-white/20 text-[10px] font-bold animate-pulse">
            ✓
          </span>
          <span className="text-xs font-bold">Ready to Matriculate</span>
          <span className="text-[11px] text-emerald-100">— all {stageLabel} prerequisites met</span>
        </div>
      )}

      {/* Stage-specific action slot (e.g. pre-approval decision buttons) */}
      {actionSlot && <div className="mt-3">{actionSlot}</div>}

      {/* Closure type selector — required at preDeparture, never auto-assumed */}
      {isClosing && readyToAdvance && !overridden && (
        <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50/70 p-3">
          <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-2">
            Closure type — required
          </div>
          <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Closure type">
            {[
              { v: 'SD', label: 'Service Delivered', hint: 'Program completed successfully' },
              { v: 'SC', label: 'Service Cancelled', hint: 'Application cancelled' },
              { v: 'SP', label: 'Service Paused', hint: 'Temporarily paused' },
            ].map((o) => (
              <button
                key={o.v}
                type="button"
                role="radio"
                aria-checked={closureType === o.v}
                onClick={() => setClosureType(o.v)}
                title={o.hint}
                className={`rounded-lg border px-3 py-2 text-left text-xs font-bold transition cursor-pointer ${
                  closureType === o.v
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-800'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                }`}
              >
                <span className="block">{o.v}</span>
                <span className="block text-[10px] font-semibold text-slate-400">{o.label}</span>
              </button>
            ))}
          </div>
          <input
            type="text"
            value={closureReason}
            onChange={(e) => setClosureReason(e.target.value)}
            placeholder="Reason (optional)"
            className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-xs text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
          />
        </div>
      )}

      {/* Actions — ADVANCE is the one primary action of the drawer:
          largest target, full-width on small screens, visually dominant.
          Everything else is a compact secondary control (2026-10-16). */}
      {shouldShowAdvanceButton(readyToAdvance, overridden) && (
        <button
          onClick={handleAdvanceClick}
          disabled={advancing}
          className="mt-3 inline-flex w-full sm:w-auto items-center justify-center gap-1.5 rounded-xl bg-success px-6 py-3 min-h-[48px] text-sm font-extrabold text-white shadow-md hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] disabled:opacity-50 cursor-pointer"
        >
          {advancing ? 'Moving…' : `${advanceLabel} →`}
        </button>
      )}
      {(isSeniorAdmin || onDraftEmail) && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {isSeniorAdmin && gates.length > 0 && (
            <button
              onClick={onOverride}
              className="inline-flex items-center gap-1 rounded-lg border border-purple-300 bg-white px-2.5 py-1.5 text-[11px] font-bold text-purple-700 hover:bg-purple-50 cursor-pointer"
            >
              Override gate
            </button>
          )}
          {isSeniorAdmin && onDemote && stage !== 'draft' && (
            <button
              onClick={onDemote}
              title={stage === 'closed' ? 'Reopen this case by moving it back to an earlier stage' : 'Move this applicant back to an earlier stage'}
              className="inline-flex items-center gap-1 rounded-lg border border-amber-300 bg-white px-2.5 py-1.5 text-[11px] font-bold text-amber-800 hover:bg-amber-50 cursor-pointer"
            >
              {stage === 'closed' ? 'Reopen case' : 'Demote'}
            </button>
          )}
          {onDraftEmail && stage !== 'closed' && (
            <button
              onClick={onDraftEmail}
              title="AI-draft a stage email to the applicant"
              className="inline-flex items-center gap-1 rounded-lg border border-violet-300 bg-white px-2.5 py-1.5 text-[11px] font-bold text-violet-700 hover:bg-violet-50 cursor-pointer"
            >
              Draft Email
            </button>
          )}
        </div>
      )}

      {unmet.length > 0 && (
        <p className="mt-2 text-[11px] text-slate-500">
          {unmet.length} prerequisite{unmet.length === 1 ? '' : 's'} outstanding
          {isSeniorAdmin ? ' — a senior admin may override.' : '.'}
        </p>
      )}
    </div>
  );
}
